import { AppBar, Box, ClickAwayListener, Drawer, Toolbar, createTheme, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import { redirectToExternalUrl } from '../../utils/ATCOUtils';

import useResponsiveHelper from '../common/hooks/useResponsiveHelper';
import BodyMainMenu from './Body/BodyMainMenu';
import HeaderGlobalButtons from './HeaderGlobalButtons';
import HeaderIcons from './HeaderIcons';
import HeaderMenu from './HeaderMenu';

interface HeaderProps {
    navigationUrl: string;
    headerJSONObject: string;
    navigationJSONValue: string;
    siteNavJSONObject: string;
    cardValues: string;
}

export interface OverallHeader {
    siteIcon?: string;
    siteIconUrl?: string;
    alternateTextForSiteIcon?: string;
    topNavigation?: string;
    storeLabel?: string;
    externalLink?: boolean;
    storeUrl?: string;
    alternateTextForStoreUrl?: string;
    searchIcon?: string;
    alternateTextForSearchIcon?: string;
    siteNavigationIcon?: string;
    alternateTextForSiteNavigationIcon?: string;
    multiSiteNavigationUrl?: string;
    userProfileIcon?: string;
    alternateTextForUserProfileIcon?: string;
    searchUrl?: string;
    firstButtonLabel?: string;
    firstButtonUrl?: string;
    secondButtonLabel?: string;
    secondButtonUrl?: string;
}

export interface Navigation {
    navText: string;
    path?: string;
    altTextPath?: string;
    icon?: string;
    altTextIcon?: string;
    external?: boolean;
    navReference?: string;
    child?: Navigation[];
    cardBean?: Card;
}

export interface Card {
    image1?: string;
    alternateTextForImage1?: string;
    image2?: string;
    alternateTextForImage2?: string;
    link1?: string;
    externalLink1?: boolean;
    link2?: string;
    externalLink2?: boolean;
    label1?: string;
    label2?: string;
}

const useStyles = makeStyles((theme) => ({
    drawerPaper: {
        marginTop: '136px',
        [theme.breakpoints.up(912)]: {
            boxShadow: '0px 9px 30px 0px rgba(0, 0, 0, 0.25) !important',
            marginTop: '136px',
            width: '1280px',
        },
        [theme.breakpoints.down(912)]: {
            boxShadow: '0px 9px 30px 0px rgba(0, 0, 0, 0.25) !important',
            width: '100vw',
            marginTop: '80px',
        },
        [theme.breakpoints.down('tablet')]: {
            marginTop: '80px',
            width: '100%',
        },
    },
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1,
    },
}));

const Header: React.FC<HeaderProps> = (props: HeaderProps) => {
    const headerObject: OverallHeader = props.headerJSONObject !== undefined ? JSON.parse(props.headerJSONObject) : {};
    const navigationObject: Navigation[] = props.navigationJSONValue !== undefined ? JSON.parse(props.navigationJSONValue) : [];
    const siteNavigationObject: Navigation[] = props.siteNavJSONObject !== undefined ? JSON.parse(props.siteNavJSONObject) : [];
    const [mainMenuclicked, setMainMenuClicked] = useState(false);
    const [mobileMenuClicked, setMobileMenuClicked] = useState(false);
    const [searchButtonClicked, setSearchButtonClicked] = useState(false);
    const [globalButtonClicked, setGlobalButtonClicked] = useState(false);
    const [drawerOpenClicked, setDrawerOpenClicked] = useState(false);
    const [menuItem, setMenuItem] = useState('');
    const theme = useTheme();
    const classes = useStyles();

    const { isCustomMobileOrTablet } = useResponsiveHelper();

    const handleCloseClick = (clicked: boolean): void => {
        setMobileMenuClicked(clicked);
        setDrawerOpenClicked(false);
    };

    const handleShadowBoxClick = () => {
        setGlobalButtonClicked(false);
        setMainMenuClicked(false);
        setDrawerOpenClicked(false);
    };

    const handleGlobalClick = (buttonClickedStatus: boolean): void => {
        setGlobalButtonClicked(buttonClickedStatus);
        setMainMenuClicked(false);
        setDrawerOpenClicked(false);
    };

    const handleMainMenuClick = (child: Navigation): void => {
        setSearchButtonClicked(false);
        setMobileMenuClicked(false);
        setDrawerOpenClicked(true);
        if (child.external) {
            redirectToExternalUrl(child.path, true);
            setMainMenuClicked(menuItem === child.navText ? !mainMenuclicked : true);
            setMenuItem(child.navText);
        } else if (!child.child) {
            redirectToExternalUrl(child.path, false);
            setMainMenuClicked(menuItem === child.navText ? !mainMenuclicked : true);
            setMenuItem(child.navText);
        } else {
            setMainMenuClicked(menuItem === child.navText ? !mainMenuclicked : true);
            setGlobalButtonClicked(false);
            setMenuItem(child.navText);
        }
    };

    if (!headerObject) {
        return <Box> Empty Header </Box>;
    }

    return (
        <ClickAwayListener onClickAway={() => setDrawerOpenClicked(false)}>
            <Box minHeight={isCustomMobileOrTablet ? '80px' : '136px'}>
                <AppBar>
                    <Toolbar
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: theme.palette.primary1,
                            paddingLeft: '40px',
                            paddingRight: '40px',
                        }}
                    >
                        <HeaderIcons
                            headerObject={headerObject}
                            searchButtonClicked={searchButtonClicked}
                            setSearchButtonClicked={setSearchButtonClicked}
                            handleGlobalClick={handleGlobalClick}
                            globalButtonClicked={globalButtonClicked}
                            setGlobalButtonClicked={setGlobalButtonClicked}
                            setMobileMenuClicked={setMobileMenuClicked}
                            setDrawerOpenClicked={setDrawerOpenClicked}
                            mobileMenuClicked={mobileMenuClicked}
                        />
                        {!isCustomMobileOrTablet && (
                            <Box width="100%" display="flex" justifyContent="flex-start">
                                <HeaderMenu
                                    navobj={navigationObject}
                                    searchButtonClicked={searchButtonClicked}
                                    handleMainMenuClick={handleMainMenuClick}
                                    mainMenuclicked={mainMenuclicked}
                                    menuItem={menuItem}
                                />
                            </Box>
                        )}
                    </Toolbar>
                </AppBar>
                {globalButtonClicked && <HeaderGlobalButtons setGlobalButtonClicked={setGlobalButtonClicked} siteNavigationObject={siteNavigationObject} />}
                {mainMenuclicked &&
                !isCustomMobileOrTablet &&
                !navigationObject.find((e: Navigation) => e.navText === menuItem)?.external &&
                navigationObject.find((e: Navigation) => e.navText === menuItem)?.child ? (
                    <>
                        {drawerOpenClicked && (
                            <div className={classes.overlay} onClick={handleShadowBoxClick}>
                                {/* Shadow overlay */}
                            </div>
                        )}
                        <Drawer
                            classes={{
                                paper: classes.drawerPaper,
                            }}
                            variant="persistent"
                            anchor="top"
                            open={drawerOpenClicked}
                            disableScrollLock
                        >
                            <BodyMainMenu navobj={navigationObject} menuItem={menuItem} isMobileOrTablet={isCustomMobileOrTablet} onCloseClick={handleCloseClick} headerobj={headerObject} />
                        </Drawer>
                    </>
                ) : null}
                {mobileMenuClicked && isCustomMobileOrTablet && !globalButtonClicked ? (
                    <>
                        {drawerOpenClicked && (
                            <div className={classes.overlay} onClick={handleShadowBoxClick}>
                                {/* Shadow overlay */}
                            </div>
                        )}
                        <Drawer
                            classes={{
                                paper: classes.drawerPaper,
                            }}
                            variant="persistent"
                            anchor="top"
                            open={drawerOpenClicked}
                            disableScrollLock
                        >
                            <BodyMainMenu navobj={navigationObject} headerobj={headerObject} menuItem={menuItem} onCloseClick={handleCloseClick} isMobileOrTablet={isCustomMobileOrTablet} />
                        </Drawer>
                    </>
                ) : null}
            </Box>
        </ClickAwayListener>
    );
};

export default Header;
